/**
 * Adapter: Wizard-Store → ScoreEngineInput.
 *
 * Die Score-Engine in `@tragfaehigkeit/core` arbeitet auf den finalen
 * `DimensionData`-Schemas und einem optionalen `FinancialDataset`. Im AP6
 * sind die meisten Dimensionen noch Stubs — wir liefern für diese
 * minimal-gültige Default-Payloads, damit die Score-Engine deterministisch
 * läuft und die Konfidenz die fehlenden Daten korrekt widerspiegelt.
 *
 * Pure Function — keine Seiteneffekte (außer einer expliziten
 * `console.warn`-Diagnose, wenn der Adapter mangels Brutto-Erwerbs-
 * einkommens auf eine Profit-Approximation zurückfällt; siehe
 * `selectBruttoForBgLuecke`).
 */

import {
  bg,
  merge,
  scoring,
  type CaseId,
  type FinancialDataset,
  type FinancialDatasetId,
  type Hours,
  type MoneyCents,
  type ResourcesData,
  type ScoreResult,
} from '@tragfaehigkeit/core';
import type { ResourcesDraft, WizardCaseState } from './wizard';
import {
  buildIdentityData,
  buildActivityData,
  buildStyleData,
  buildHealthData,
  buildPrivateData,
} from './dimensionScoreInput';

// Die qualitative resources-Dimension ist separat vom (einstufigen)
// FinancialDataset — sie trägt keine Freitext-Felder im Wizard und bleibt leer.
const EMPTY_RESOURCES: ResourcesData = {
  dimension: 'resources',
};

/**
 * Baut aus dem Resources-Slice ein FinancialDataset, wenn genug Daten da sind.
 * Liefert undefined, wenn die Daten noch nicht für eine Score-Berechnung
 * ausreichen (mind. 1 Monat Aggregat braucht das Schema).
 *
 * Wir spiegeln den eingegebenen Monatswert auf einen einzigen Aggregate-
 * Eintrag — Mehr-Monats-BWA-Eingabe ist Phase-2-Scope.
 */
export function buildFinancialDatasetFromDraft(
  r: ResourcesDraft,
  caseId: string,
): FinancialDataset | undefined {
  const hasRevenueOrCosts = r.revenueCents !== null || r.costsCents !== null;
  if (!hasRevenueOrCosts) return undefined;

  const revenue = (r.revenueCents ?? 0) as MoneyCents;
  const costs = (r.costsCents ?? 0) as MoneyCents;

  // Stichmonat YYYY-MM des aktuellen Datums — Aggregat-Mindesteintrag.
  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const liquidity =
    r.liquidityCents !== null
      ? {
          liquidAssetsCents: r.liquidityCents as MoneyCents,
          shortTermLiabilitiesCents: (r.shortTermLiabilitiesCents ?? 0) as MoneyCents,
          asOf: now,
        }
      : undefined;

  const dataset: FinancialDataset = {
    id: `fd_local_${caseId}` as unknown as FinancialDatasetId,
    caseId: caseId as unknown as CaseId,
    monthlyAggregates: [
      {
        yearMonth: ym,
        revenueCents: revenue,
        costsCents: costs,
      },
    ],
    hourlyRate: {
      ...(r.hoursPerMonth !== null ? { billableHoursPerMonth: r.hoursPerMonth as Hours } : {}),
    },
    ...(liquidity ? { liquidity } : {}),
    // § 11-Anrechnung + echte Burn-Rate (0032). Optionale Felder nur setzen,
    // wenn erfasst — exactOptionalPropertyTypes.
    ...(r.bruttoErwerbseinkommenCents !== null
      ? { bruttoErwerbseinkommenCents: r.bruttoErwerbseinkommenCents as MoneyCents }
      : {}),
    ...(r.sonstigesEinkommenCents !== null
      ? { sonstigesEinkommenCents: r.sonstigesEinkommenCents as MoneyCents }
      : {}),
    ...(r.burnRateCents !== null ? { monthlyBurnRateCents: r.burnRateCents as MoneyCents } : {}),
    bgComposition: {
      members:
        r.bgComposition.length > 0
          ? r.bgComposition.map((m) => ({
              role: m.role,
              age: m.age,
              specialNeeds: [...m.specialNeeds],
            }))
          : [{ role: 'antragsteller' as const, age: 35, specialNeeds: [] }],
      kdu: {
        kaltmiete: r.kdu.kaltCents as MoneyCents,
        nebenkosten: r.kdu.nebenCents as MoneyCents,
        heizkosten: r.kdu.heizCents as MoneyCents,
      },
    },
    sourceType: 'manual',
    uploadedFileNames: [],
    createdAt: now,
    updatedAt: now,
  };

  return dataset;
}

/**
 * Wählt das Brutto-Erwerbseinkommen (§ 11 SGB II) für die SGB-II-Anrechnung.
 *
 * Bevorzugt den first-class-Wert aus dem `FinancialDataset` (sobald der
 * Wizard ein eigenes Brutto-Feld hat oder ein BWA-Parser den Wert
 * extrahiert). Solange kein expliziter Wert vorliegt, fällt der Adapter
 * auf `max(0, revenue − costs)` zurück. Diesen Pfad MARKIEREN wir mit
 * einer Console-Warnung — er liefert eine Schätzung, keine belastbare
 * Anrechnungs-Grundlage. Die formelle Stellungnahme darf nie auf einer
 * Schätzung basieren.
 *
 * Returns `{ valueCents, isEstimate }` — Aufrufer können den Hinweis im
 * UI signalisieren oder die Stellungnahme blockieren.
 */
export interface BruttoSelection {
  readonly valueCents: MoneyCents;
  readonly isEstimate: boolean;
}

export function selectBruttoForBgLuecke(
  r: ResourcesDraft,
  explicitBruttoCents?: number | null,
): BruttoSelection {
  if (typeof explicitBruttoCents === 'number' && Number.isFinite(explicitBruttoCents)) {
    return {
      valueCents: Math.max(0, Math.round(explicitBruttoCents)) as MoneyCents,
      isEstimate: false,
    };
  }
  const profit =
    r.revenueCents !== null && r.costsCents !== null ? r.revenueCents - r.costsCents : 0;
  // Bewusst lautes Logging: dieser Pfad ist ein Notbehelf, kein Default-
  // Verhalten, das wir perpetuieren wollen. Sobald das Wizard-Schema ein
  // Brutto-Feld bekommt, fällt diese Warnung weg.
  console.warn(
    '[scoreInput] selectBruttoForBgLuecke: kein Brutto-Erwerbseinkommen erfasst — ' +
      'fallback auf max(0, revenue − costs). § 11 SGB II rechnet auf Brutto, ' +
      'nicht auf Gewinn. Wert ist eine Schätzung und für die formelle Stellungnahme nicht zulässig.',
  );
  return {
    valueCents: Math.max(0, profit) as MoneyCents,
    isEstimate: true,
  };
}

/**
 * Baut den effektiven BG-Gap aus dem Resources-Slice, falls genug Daten da sind.
 * Wenn keine BWA-Daten vorhanden sind, wird die Lücke = Bedarf zurückgegeben
 * (volle Lücke, weil kein Einkommen).
 */
export function computeBgGapCentsFromDraft(r: ResourcesDraft): number | undefined {
  if (r.bgComposition.length === 0) return undefined;
  // Echtes Brutto-Erwerbseinkommen bevorzugen; nur wenn nicht erfasst, schätzt
  // selectBruttoForBgLuecke aus dem Gewinn (mit lauter Warnung).
  const brutto = selectBruttoForBgLuecke(r, r.bruttoErwerbseinkommenCents);
  const luecke = bg.computeTragfaehigkeitsLuecke({
    bg: {
      members: r.bgComposition.map((m) => ({
        role: m.role,
        age: m.age,
        specialNeeds: [...m.specialNeeds],
      })),
      kdu: {
        kaltmiete: r.kdu.kaltCents as MoneyCents,
        nebenkosten: r.kdu.nebenCents as MoneyCents,
        heizkosten: r.kdu.heizCents as MoneyCents,
      },
    },
    bruttoErwerbseinkommenCents: brutto.valueCents,
    ...(r.sonstigesEinkommenCents !== null
      ? { sonstigesEinkommenCents: r.sonstigesEinkommenCents as MoneyCents }
      : {}),
  });
  return luecke.luckeCents;
}

export function buildScoreResultFromCase(c: WizardCaseState | undefined): ScoreResult {
  const r = c?.resources;
  const fd = r ? buildFinancialDatasetFromDraft(r, c?.caseId ?? 'demo') : undefined;
  const bgGapCents = r ? computeBgGapCentsFromDraft(r) : undefined;

  // Wirksame (gemergte) Werte der fünf Freitext-Dimensionen: Berater-Angabe
  // wenn vorhanden, sonst Mandanten-Angabe (Zwei-Ebenen-Modell, core `merge`).
  // Financial/Resources bleibt einstufig (Mandant) — siehe supabaseSync.
  const md = c?.dimensionData;
  const bd = c?.beraterDimensionData;
  const eff = (dim: keyof NonNullable<typeof md>) =>
    merge.effectiveFields(md?.[dim]?.fields, bd?.[dim]?.fields);

  return scoring.computeScoreResult({
    identity: buildIdentityData(eff('identity')),
    activity: buildActivityData(eff('activity')),
    style: buildStyleData(eff('style')),
    resources: EMPTY_RESOURCES,
    health: buildHealthData(eff('health')),
    private: buildPrivateData(eff('private')),
    ...(fd ? { financialDataset: fd } : {}),
    ...(bgGapCents !== undefined ? { bgGapCents } : {}),
    computedAt: new Date(0), // deterministisch — UI berechnet bei jedem Render neu
  });
}
